/**
 * Xinkit — public-survey adapter for `g_game.code = 'xinkit'`.
 *
 * It was called `plinko`. "Plinko" is a registered game-show trademark, so it
 * was retired from the product entirely — visible name AND code — in migration
 * 092. The note that used to sit here said the code stayed because of a
 * `bs_event.gameId` FK: that was wrong. The column points at `g_game.id`,
 * which is still 3 and was never touched. The visual is the design-authored
 * drop-and-bounce canvas (`_vendor/XinkitCanvas`).
 *
 * Picks the winner once via the shared `pickPrize`, resolves the brand, and
 * bridges the canvas `onRevealed(prize, bucketIndex)` → `onWin`. Visuals and
 * the honest-scatter physics live in the canvas.
 */
import { useMemo } from 'react';
import type { GamePlayProps } from './pickPrize';
import { pickPrize } from './pickPrize';
import { Xinkit as XinkitCanvas, type BallVariant } from './_vendor/XinkitCanvas';
import { resolveBrand } from './_shared/resolveBrand';
import { gameLang, toSegment } from './_shared/segments';
import { GameEmpty } from './_shared/GameEmpty';

export function Xinkit({ items, primary, accent, onWin, lang, logo, config }: GamePlayProps) {
  const valid = useMemo(() => items.filter((i) => i.available > 0), [items]);
  const winnerIdx = useMemo(() => pickPrize(valid), [valid]);

  if (valid.length === 0 || winnerIdx < 0) return <GameEmpty />;

  const winner = valid[winnerIdx];
  const brand = resolveBrand({ primary, accent, logo });

  return (
    <div className="flex w-full justify-center">
      <XinkitCanvas
        prize={toSegment(winner)}
        brand={brand}
        ballVariant={(config?.ballVariant as BallVariant | undefined) ?? 'logo'}
        wordmark={typeof config?.wordmark === 'string' ? config.wordmark : undefined}
        tagline={typeof config?.tagline === 'string' ? config.tagline : undefined}
        language={gameLang(lang)}
        onRevealed={() => onWin(winner)}
      />
    </div>
  );
}
